import React, { useState, useEffect } from 'react';
import axios from 'axios';
import MasterLayout from '../layouts/masterlayout';
import ErrorMessage from '../common/errormessage';
import Spinner from '../common/spinner';
import routes from '../../routes';
import PaginationControl from '../common/pagination_control';
import GeneralButton from '../common/button_general';
import TableHeader from '../common/table_header';
import usePaginationAndSorting from '../../hooks/usepaginationandsorting';
import { useNavigate, useLocation } from 'react-router-dom';
import { getHeaders } from '../../lib/requests';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import NoResultsFound from '../common/noresultsfound';
import GeneralizedTableRow from '../common/table_row';

function UserList() {
    const { page, pageSize, sortBy, handlePageChange, handleSortClick } = usePaginationAndSorting('desc_UserLastModifiedDate');
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    //const [totalPages, setTotalPages] = useState(0);
    //const [setTotalRecords] = useState(0);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const navigate = useNavigate();

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const searchQueryParam = queryParams.get('search');

    const [searchQuery] = useState(searchQueryParam || '');

    useEffect(() => {
        const fetchUsers = async () => {
            if (!pageSize) return;
            setIsLoading(true);
            try {
                const apiToken = localStorage.getItem('accessToken');
                const apiUrl = process.env.REACT_APP_API_URL;
                const response = await axios.get(`${apiUrl}/users`, {
                    headers: getHeaders(apiToken),
                    params: { page, pageSize, ...(sortBy && { order_by: sortBy }), ...(searchQuery && { search: searchQuery }) },
                });

                // Assuming the data you need is in response.data.users
                if (response.data.data && Array.isArray(response.data.data)) {
                    setUsers(response.data.data);
                    // setTotalPages(response.data.totalPages); // Uncomment and adjust if pagination data is available
                    // setTotalRecords(response.data.totalRecords); // Uncomment and adjust if total records count is available
                } else {
                    setUsers([]); // Ensures users is always an array
                    setShowError(true);
                    setErrorMessage('Invalid data structure received from API');
                }

            } catch (error) {
                setShowError(true);
                setErrorMessage('Failed to fetch users');
            } finally {
                setIsLoading(false);
            }
        };
        fetchUsers();
    }, [page, pageSize, sortBy, searchQuery]);

    const currentRoute = routes.find(route => route.path === location.pathname);
    const routePageTitle = currentRoute ? currentRoute.pageTitle : '';
    const pageTitle = routePageTitle ? routePageTitle : 'User List';

    const columns = [
        { id: 'Username', name: 'Username' },
        { id: 'Attributes.name', name: 'Name' },
        { id: 'Attributes.email', name: 'Email' },
        { id: 'Attributes.phone_number', name: 'Phone Number' },
        { id: 'Attributes.email_verified', name: 'Verified',
            format: (value) => value ? 'Yes' : 'No'
         },
        { id: 'Enabled', name: 'Enabled',
            format: (value) => value ? 'Yes' : 'No'
         },
        { id: 'UserStatus', name: 'User Status' },
        { id: 'UserCreateDate', name: 'Create Date', format: (value) => new Date(value).toLocaleString()},
        { id: 'UserLastModifiedDate', name: 'Last Modified Date', format: (value) => new Date(value).toLocaleString()},        
        {
          id: 'edit', name: 'Edit', textColor: 'text-custom-blue',
          link: (User) => `/users/${User.Username}`, icon: PencilSquareIcon, tooltip: 'Edit User'
        },
    ];

    return (
        <MasterLayout pageTitle={pageTitle} searchQuery={searchQuery}>
            {showError && <ErrorMessage message={errorMessage} />}
            <div className="px-2 sm:px-3 lg:px-3">
                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-2 flex justify-between">
                    <h1 className="text-lg font-semibold text-gray-900">{pageTitle}</h1>
                    <GeneralButton type="submit" buttonType="primary" onClick={() => {
                        const baseUrl = '/users/create';
                        navigate(baseUrl);
                    }}>
                        Create User
                    </GeneralButton>
                </div>
                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-2">
                    <table className="min-w-full divide-y divide-gray-300">
                        <TableHeader columns={columns} handleSortClick={handleSortClick} currentSort={sortBy} />
                        <tbody className="divide-y divide-gray-200 bg-white">
                            {isLoading ? (
                                <tr>
                                    <td colSpan={columns.length} className="text-center">
                                        <Spinner minHeight={630} />
                                    </td>
                                </tr>
                            ) : users.length > 0 ? (users.map((user) => (
                                <GeneralizedTableRow key={user.Username} rowData={user} columns={columns} />
                            )))
                                : (
                                    <NoResultsFound message={"No " + pageTitle.toLocaleLowerCase() + " found"} minHeight={630} columSpan={columns.length}  />
                                )}
                        </tbody>
                    </table>
                    {!isLoading ? (<PaginationControl
                        page={page}
                        recordsPerPage={pageSize}
                        totalRecords={0}
                        totalPages={0}
                        handlePageChange={handlePageChange}
                    />): null}
                </div>
            </div>
        </MasterLayout>

    );
}

export default UserList;
